import {
    EntityFactory,
    EntityRef,
    PageLibraryInterface,
    PageLibrary,
    EntityType
  } from "@amzn/ask-legal-domain";
import { UIModel } from "./ui-model";
import * as React from "react";
import { Builder } from "builder-pattern";

export namespace PageLibraryModel {
    export const TITLE_CHAR_LIMIT = 50;

    export class CreateState {
        name: UIModel.State<string>;
        displayName: UIModel.State<string>;
        freshness: UIModel.State<string>;
        instanceRef: EntityRef;
        shared: UIModel.State<boolean>;
        restrictToLegal: UIModel.State<boolean>;
        enableApprovalWorkflow: UIModel.State<boolean>;
        permissionRequestTemplateUrl: UIModel.State<string>;
        errorText: string;
        reset: () => void;

        static toInput (state: CreateState): PageLibraryInterface.CreatePageLibraryInput {
            return PageLibraryInterface.CreatePageLibraryInput.create(
                state.instanceRef,
                state.name.value,
                state.displayName.value,
                parseInt(state.freshness.value),
                state.restrictToLegal.value,
                state.shared.value,
                state.enableApprovalWorkflow.value,
                state.permissionRequestTemplateUrl.value
            );
        }

        static use (props: {
            template?: PageLibraryInterface.CreatePageLibraryInput
            instanceId: string
        }): CreateState {
            const name = UIModel.State.useWithRegexAndCharLimit({
                regex: new RegExp(/^[a-zA-Z0-9]+$/),
                characterLimit: TITLE_CHAR_LIMIT
            });
            const displayName = UIModel.State.useNotNullStringWithCharLimit({ characterLimit: TITLE_CHAR_LIMIT });
            const freshness = UIModel.State.use<string>({
                initialValue: "30"
            });

            const restrictToLegal = UIModel.State.use<boolean>({
                initialValue: false
            });

            const shared = UIModel.State.use<boolean>({
                initialValue: false
            });

            const enableApprovalWorkflow = UIModel.State.use<boolean>({
                initialValue: false
            });

            const permissionRequestTemplateUrl = UIModel.State.use<string>({
                initialValue: ""
            });

            const reset = () => {
                name.setValue(null);
                displayName.setValue(null);
                freshness.setValue("30");
                restrictToLegal.setValue(false);
                shared.setValue(false);
                enableApprovalWorkflow.setValue(false);
                permissionRequestTemplateUrl.setValue("");
            };

            React.useEffect(() => {
                if (!props.template) return;
                name.setValue(props.template.name);
                displayName.setValue(props.template.displayName);
                freshness.setValue(props.template.freshness.toString());
                restrictToLegal.setValue(props.template.restrictToLegal);
                shared.setValue(props.template.shared);
                enableApprovalWorkflow.setValue(props.template.enableApprovalWorkflow);
                permissionRequestTemplateUrl.setValue(props.template.permissionRequestTicketTemplate);
            }, []);

            return Builder<CreateState>(new CreateState())
                .name(name)
                .displayName(displayName)
                .freshness(freshness)
                .restrictToLegal(restrictToLegal)
                .shared(shared)
                .enableApprovalWorkflow(enableApprovalWorkflow)
                .permissionRequestTemplateUrl(permissionRequestTemplateUrl)
                .instanceRef(
                    EntityFactory.fromEntityAttributes(props.instanceId, EntityType.Instance, 1)
                )
                .reset(reset)
                .build();
        }
    }

    export class UpdateState {
        targetRef: EntityRef;
        displayName: UIModel.State<string>;
        freshness: UIModel.State<string>;
        restrictToLegal: UIModel.State<boolean>;
        shared: UIModel.State<boolean>;
        enableApprovalWorkflow: UIModel.State<boolean>;
        permissonRequestTemplateUrl: UIModel.State<string>;
        azaIntegrationEnabled: UIModel.State<boolean>;
        errorText: string;
        reset: () => void;
        isDirty: () => boolean;

        static toInput (state: UpdateState): PageLibraryInterface.UpdatePageLibraryInput {
            return PageLibraryInterface.UpdatePageLibraryInput.create(
                state.targetRef,
                {
                    displayName: state.displayName.value,
                    freshness: parseInt(state.freshness.value),
                    restrictToLegal: state.restrictToLegal.value,
                    shared: state.shared.value,
                    enableApprovalWorkflow: state.enableApprovalWorkflow.value ?? false,
                    permissionRequestTicketTemplate: state.permissonRequestTemplateUrl.value ?? "",
                    azaIntegrationEnabled: state.azaIntegrationEnabled.value ?? false
                }
            );
        }

        static use (props: {
            template: PageLibrary
        }): UpdateState {
            const displayName = UIModel.State.useNotNullStringWithCharLimit({ characterLimit: TITLE_CHAR_LIMIT });
            const freshness = UIModel.State.useNotNullString({});
            const shared = UIModel.State.useRequired<boolean>({});
            const restrictToLegal = UIModel.State.useRequired<boolean>({});
            const enableApprovalWorkflow = UIModel.State.useRequired<boolean>({});
            const permissionRequestTicketTemplateUrl = UIModel.State.use<string>({});
            const azaIntegrationEnabled = UIModel.State.use<boolean>({
                initialValue: false
            });

            const reset = () => {
                displayName.setValue(props.template.name);
                freshness.setValue(props.template.freshness.toString());
                shared.setValue(shared.value);
                restrictToLegal.setValue(restrictToLegal.value);
                enableApprovalWorkflow.setValue(props.template.enableApprovalWorkflow);
                permissionRequestTicketTemplateUrl.setValue(props.template.permissionRequestTicketTemplate);
                azaIntegrationEnabled.setValue(!!props.template.azaIntegrationEnabled);
            };

            const isDirty = () => {
                if (
                    displayName.value === props.template.name &&
                    freshness.value === props.template.freshness.toString() &&
                    restrictToLegal.value === props.template.restrictToLegal &&
                    shared.value === props.template.shared &&
                    enableApprovalWorkflow.value === props.template.enableApprovalWorkflow &&
                    permissionRequestTicketTemplateUrl.value === props.template.permissionRequestTicketTemplate &&
                    azaIntegrationEnabled.value === !!props.template.azaIntegrationEnabled
                ) return false;
                return true;
            };

            React.useEffect(() => {
                displayName.setValue(props.template.name);
                freshness.setValue(props.template.freshness.toString());
                shared.setValue(props.template.shared);
                restrictToLegal.setValue(props.template.restrictToLegal);
                enableApprovalWorkflow.setValue(props.template.enableApprovalWorkflow);
                permissionRequestTicketTemplateUrl.setValue(props.template.permissionRequestTicketTemplate);
                azaIntegrationEnabled.setValue(!!props.template.azaIntegrationEnabled);
            }, []);

            return Builder<UpdateState>(new UpdateState())
                .targetRef(EntityFactory.toEntityRef(props.template))
                .displayName(displayName)
                .freshness(freshness)
                .shared(shared)
                .restrictToLegal(restrictToLegal)
                .enableApprovalWorkflow(enableApprovalWorkflow)
                .permissonRequestTemplateUrl(permissionRequestTicketTemplateUrl)
                .azaIntegrationEnabled(azaIntegrationEnabled)
                .reset(reset)
                .isDirty(isDirty)
                .build();
        }
    }
}